import { useRouter } from 'next/router';
import { useAuth } from '../../context/AuthUserContext';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Rating from '../atoms/Rating';
import Button from '../atoms/Button';
import Logo from '../atoms/Logo';
import Tags from './Tags';
import ItemReviewReplyForm from '../organisms/ItemReviewReplyForm';
import styles from '../../styles/components/molecules/Feedback.module.scss';
import { Review } from '@prisma/client';

interface Data extends Review {
  rating: number;
  reply: string;
}

interface FeedbackProps {
  className?: string;
  access?: boolean;
  restricted?: boolean;
  preview?: boolean;
  setLayer?: (el: any) => void;
  data: Data;
}

export default function Feedback({
  data,
  setLayer,
  access,
  restricted,
  className,
}: FeedbackProps) {
  const { t } = useTranslation(['common', 'rating']);
  const { locale } = useRouter();
  const { user } = useAuth();
  const {
    id,
    contact_name,
    contact_type,
    createdAt,
    event,
    review,
    rating,
    headline,
    reply,
    venue_id,
  } = data;
  const itemType = !!venue_id ? t(`rating:venue`) : t(`rating:service`);
  const role = contact_type.toLowerCase();
  const author = (
    <span>
      <b>{`${headline || contact_name}`}</b>
      {`, ${role}`}
    </span>
  );
  const referDate = event ? new Date(event) : undefined;
  const eventDate = referDate
    ? referDate.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        // day: 'numeric',
      })
    : undefined;

  const createDate = new Date(createdAt);
  const reviewDate = createDate.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const tags = [
    { label: reviewDate, dark: true },
    !!eventDate
      ? {
          label: `${t(`rating:event_date_prefix`, { itemType })} ${eventDate}`,
        }
      : null,
  ].filter((el) => !!el) as any;

  const hl = review ? `${review.split(' ').slice(0, 3).join(' ')}...` : '';
  const p = review; //`...${review?.split(' ').slice(3).join(' ')}`;
  const itemTypeLabel = !!venue_id ? 'Venue' : 'Service Provider';
  const mailto = `mailto:info@venuescout.org?subject=Report%20Request%20for%20${itemTypeLabel}%20Review%20[${id}]&cc=${user?.email}`;
  const classes = clsx(styles.feedback, className);

  return (
    <div className={classes}>
      {access && <Button text icon={'report'} label={'Report'} href={mailto} />}
      <Tags data={tags} />
      {!!hl && (
        <h3>
          <b>{hl}</b>
        </h3>
      )}
      {!!p && <q>{p}</q>}
      <br />
      <br />
      <Rating small value={rating} label={author} />
      {reply && (
        <div className={styles.reply}>
          <small>
            <b>
              {t(`rating:reply:label`, { itemType: itemType.toLowerCase() })}:
            </b>
          </small>

          <q>{reply}</q>
        </div>
      )}
      {access && !reply && (
        <Button
          small
          more
          colored
          label={t(`rating:cta-reply`)}
          // href={restricted ? `/venue-packages?venue_id=${venue_id}` : undefined}
          onClick={
            setLayer
              ? () =>
                  setLayer(
                    restricted
                      ? {
                          bg: true,
                          center: true,
                          auto: true,
                          children: (
                            <div>
                              <Logo large noLink center />
                              {/* <h2>{t(`rating:reply:upgrade-h`)}</h2> */}
                              <p>{t(`rating:reply:upgrade-p`)}</p>
                              <p></p>
                              <Button
                                large
                                dark
                                colored
                                icon="rocket"
                                label={t(`common:cta-upgrade`)}
                                onClick={() => setLayer(null)}
                                href={
                                  restricted
                                    ? `/venue-packages?venue_id=${venue_id}`
                                    : undefined
                                }
                              />
                            </div>
                          ),
                        }
                      : {
                          bg: true,
                          large: true,
                          forced: true,
                          className: styles.reviewFormLayer,
                          children: (
                            <ItemReviewReplyForm
                              type={!!venue_id ? 'venue' : 'service'}
                              data={data}
                              onComplete={() => setLayer(undefined)}
                            />
                          ),
                        }
                  )
              : undefined
          }
        />
      )}
    </div>
  );
}
